import { Alert, Button, Checkbox, Col, Form, FormInstance, Input, InputNumber, message, Row, Select, Collapse, Divider, Tooltip, Tag } from "antd"
import TextArea from "antd/es/input/TextArea"
import React, { useEffect, useRef, useState } from "react"
import { useRegister } from "../../contexts/RegisterContext"
import { formatInputNumber, parseInputNumber, roundValue } from "../../utils"
import { MinusCircleOutlined, BarcodeOutlined, SettingOutlined, ToolOutlined, InfoCircleOutlined, DeploymentUnitOutlined } from "@ant-design/icons"
import BarcodeScannerModal from "../modals/BarcodeScannerModal"
import { useBarcodeScanner } from "../../hooks/useBarcodeScanner"
import { useConfig } from "../../contexts/ConfigContext"
import { modules } from "../../config/pages"
import { TElementModuleOptions } from "../../models/storageModel"
import ItemImageUploader from "../common/ItemImageUploader"

type FormProps = {
  form: FormInstance
  getFormTaxes: (values: any, type: "sell" | "purchase") => Partial<TElementModuleOptions>[]
}

function RegisterFixedAssetForm({ form, getFormTaxes }: Readonly<FormProps>) {
  const { Option } = Select
  const { register } = useRegister()
  const { config } = useConfig()
  const mod = config.module
  const { currentStorageItem: currentRegister } = register

  // Counter for dynamic IDs
  const idCounter = useRef(-1)
  const getNextId = () => --idCounter.current

  const [purchaseTaxes, setPurchaseTaxes] = useState([{ id: -1 }])
  const [otherValues, setOtherValues] = useState([{ id: -1 }])
  const [purchaseTaxesWarning, setPurchaseTaxesWarning] = useState(false)
  const [otherValuesWarning, setOtherValuesWarning] = useState(false)
  const [isScannerOpen, setIsScannerOpen] = useState(false)

  const formImages = Form.useWatch("images", form)

  const categories = register.categoryOptions.filter((el) => {
    if (mod === modules.EditStorageItem && el.key === currentRegister?.category) {
      return true
    }
    return el.show !== false
  })

  const purchaseTaxesOptions = register.taxesOptions.filter((el) => {
    if (mod === modules.EditStorageItem) {
      const isAssigned = currentRegister?.module_options?.some(
        (opt) => opt.option_id === el.id && opt.sub_type === "PURCHASE_TAX"
      )
      if (isAssigned) return true
    }
    return el.show !== false
  })

  const presentations = register.displayOptions.filter((el) => {
    if (mod === modules.EditStorageItem && el.key === currentRegister?.display) {
      return true
    }
    return el.show !== false
  })

  const ums = register.umOptions.filter((el) => {
    if (mod === modules.EditStorageItem && el.key === currentRegister?.measure_unit) {
      return true
    }
    return el.show !== false
  })

  // Global barcode scanner handling
  const handleBarcodeScanned = (scannedBarcode: string) => {
    const currentBarcode = form.getFieldValue("barcode")
    if (!currentBarcode) {
      form.setFieldsValue({ barcode: scannedBarcode })
    }
  }

  useBarcodeScanner({ onScan: handleBarcodeScanned, isActive: !isScannerOpen })

  useEffect(() => {
    const existingPurchaseTaxes =
      currentRegister?.module_options
        ?.filter((el) => el.sub_type === "PURCHASE_TAX")
        .map((el) => ({ id: el.id || -1 })) || []
    const existingOtherValues =
      currentRegister?.other_values?.map((el) => ({ id: el.id || -1 })) || []

    if (mod === modules.EditStorageItem) {
      if (existingPurchaseTaxes.length) {
        setPurchaseTaxes(existingPurchaseTaxes)
      }
      if (existingOtherValues.length) {
        setOtherValues(existingOtherValues)
      }
      if (currentRegister?.purchase_price) {
        handlePriceChange({ purchase_price: currentRegister.purchase_price })
      }
      if (currentRegister?.is_traceable !== undefined) {
        form.setFieldsValue({ is_traceable: Boolean(currentRegister.is_traceable) })
      } else {
        form.setFieldsValue({ is_traceable: true })
      }
    } else {
      // Al crear un nuevo activo fijo, asegurar que trazabilidad esté habilitada por defecto
      form.setFieldsValue({ is_traceable: true })
    }
  }, [register, mod])

  const scanForEmptyFields = (data: any[], setData: any, setShowWarning: any, key: string) => {
    const values = form.getFieldsValue()
    let emptyValues = false
    for (const str in values) {
      emptyValues = str.includes(key) && values[str] === undefined
      if (emptyValues) {
        break
      }
    }
    if (emptyValues) {
      setShowWarning(true)
    } else {
      setData([...data, { id: getNextId() }])
      setShowWarning(false)
    }
  }

  const removePurchaseTaxes = (id: number) => {
    if (id > 0) {
      message.error("No se puede eliminar un impuesto que ya se encuentra registrado")
      return
    }
    const filterResult = purchaseTaxes.filter((item) => item.id !== id)
    const result = filterResult.length ? filterResult : [{ id: getNextId() }]
    form.setFieldsValue({
      [`purchase_taxes_${id}`]: undefined,
      [`purchase_taxes_active_${id}`]: undefined,
      purchase_price_net: undefined,
      purchase_price_full: undefined,
    })
    setPurchaseTaxes(result)
  }

  const removeOtherValues = (id: number) => {
    if (id > 0) {
      message.error("No se puede eliminar un campo que ya se encuentra registrado")
      return
    }
    const filterResult = otherValues.filter((item) => item.id !== id)
    const result = filterResult.length ? filterResult : [{ id: getNextId() }]
    form.setFieldsValue({
      [`otherValues_type_${id}`]: undefined,
      [`otherValues_value_${id}`]: undefined,
      [`otherValues_visible_${id}`]: undefined,
    })
    setOtherValues(result)
  }

  const allValues = Form.useWatch([], form)
  const purchaseDiscountValue = Form.useWatch("purchase_discount", form)

  const disabledOption = (option: string, field: "purchase_taxes" | "sell_taxes") => {
    const values = form.getFieldsValue()
    for (const key in values) {
      if (key.startsWith(field) && !key.includes("active") && values[key] === option) {
        return true
      }
    }
    return false
  }

  const isFieldDisabled = (id: number, prefix: string) => {
    if (id <= 0) return false
    return (
      allValues?.[`${prefix}_active_${id}`] === false ||
      allValues?.[`${prefix}_visible_${id}`] === false
    )
  }

  const handlePriceChange = (overrides: Record<string, any> = {}) => {
    const formValues = { ...form.getFieldsValue(), ...overrides }
    const value = formValues.purchase_price || 0
    const AllTaxes = getFormTaxes({ ...formValues }, "purchase")
      .filter((el) => el.is_active)
      .map((el) => register.taxesOptions.find((tax) => tax.id === el.option_id))
      .filter(Boolean)
    const rateTax = AllTaxes.filter((el) => el?.type === "TAX_RATE").reduce(
      (sum, el) => sum + (el?.numeric_value || 0),
      0
    )
    const fixedTax = AllTaxes.filter((el) => el?.type === "TAX_FIXED").reduce(
      (sum, el) => sum + (el?.numeric_value || 0),
      0
    )
    const discountRate = formValues.purchase_discount || 0
    const fullPrice = roundValue(value * (1 + rateTax / 100) + fixedTax)
    const netPrice = roundValue(value * (1 - discountRate / 100) * (1 + rateTax / 100) + fixedTax)
    form.setFieldsValue({ purchase_price_full: fullPrice, purchase_price_net: netPrice })
  }

  const isBarcodeDisabled =
    mod === modules.EditStorageItem &&
    currentRegister?.barcode !== undefined &&
    currentRegister.barcode.length > 0
  const isEdit = mod === modules.EditStorageItem
  const hasAdvancedData = Boolean(
    currentRegister?.sku ||
    currentRegister?.location ||
    purchaseTaxes.some((t) => t.id > 0) ||
    otherValues.some((o) => o.id > 0)
  )
  const defaultActiveCollapse = isEdit && hasAdvancedData ? ["advanced-options"] : []

  return (
    <>
      {/* ── Sección 1: Identificación del Activo Fijo / Equipo ── */}
      <div style={{ marginTop: "1rem" }}>
        <h5 style={{ fontWeight: 600, fontSize: "1rem", color: "#1f1f1f", marginBottom: "1rem" }}>
          <ToolOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          Identificación del Activo Fijo / Equipo
        </h5>

        {/* Galería de Imágenes */}
        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <Form.Item name="images" style={{ marginBottom: 12 }}>
              <ItemImageUploader
                elementId={currentRegister?.id}
                elementUid={currentRegister?.uid}
                value={formImages || currentRegister?.images || (currentRegister?.image ? [{ file_name: currentRegister.name || "Imagen principal", file_url: currentRegister.image, is_primary: true, display_order: 0, storage_path: "" }] : [])}
                onChange={(imgs, primaryUrl) => {
                  form.setFieldsValue({
                    images: imgs,
                    image: primaryUrl || "",
                    image_mini: primaryUrl || "",
                  })
                }}
              />
            </Form.Item>
            <Form.Item name="image" hidden><Input /></Form.Item>
            <Form.Item name="image_mini" hidden><Input /></Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={{ span: 16 }}>
            <Form.Item
              label="Nombre del Activo / Equipo"
              name="name"
              rules={[{ required: true, message: "Por favor ingresa el nombre del equipo o activo" }]}
            >
              <Input placeholder="Ej. Torno Industrial CNC, Báscula Ganadera Digital, Tractor John Deere..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item
              label="Código Interno / Placa"
              name="custom_id"
              tooltip="Identificador o código contable de activo fijo (Ej. AF-001, EQ-MEC-05)"
            >
              <Input
                placeholder="Ej. AF-001"
                disabled={
                  mod === modules.EditStorageItem &&
                  currentRegister?.custom_id !== undefined &&
                  currentRegister?.custom_id.length > 0
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="Código de barras" name="barcode">
              <Input
                disabled={isBarcodeDisabled}
                placeholder="Escanea o ingresa código"
                suffix={
                  <BarcodeOutlined
                    onClick={() => !isBarcodeDisabled && setIsScannerOpen(true)}
                    style={{
                      cursor: isBarcodeDisabled ? "not-allowed" : "pointer",
                      color: isBarcodeDisabled ? "#bfbfbf" : "#1890ff",
                    }}
                  />
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="SKU / Modelo / Referencia" name="sku">
              <Input placeholder="Ej. MODEL-X200, REF-8849" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="Ubicación Física / Planta" name="location">
              <Input placeholder="Ej. Taller Central, Planta 1, Lote 3" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <Form.Item label="Descripción Técnica / Especificaciones Generales" name="description">
              <TextArea
                rows={2}
                placeholder="Detalla especificaciones técnicas, capacidad operativa, marca del fabricante, etc."
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ── Banner de Trazabilidad y Control Serializado ── */}
        <div
          style={{
            background: "#f0f5ff",
            padding: "12px 16px",
            borderRadius: "8px",
            border: "1px solid #adc6ff",
            marginBottom: "1.5rem",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col xs={24} sm={18}>
              <Form.Item
                name="is_traceable"
                valuePropName="checked"
                style={{ margin: 0 }}
                tooltip="Habilita el seguimiento individual por número de serie, placa de activo, hoja de vida y mantenimientos."
              >
                <Checkbox>
                  <span style={{ fontWeight: 600, color: "#1d39c4", fontSize: "14px" }}>
                    <DeploymentUnitOutlined style={{ marginRight: 6 }} />
                    Habilitar Trazabilidad / Control Individual (Placa, Serial o Hoja de Vida)
                  </span>
                </Checkbox>
              </Form.Item>
              <div style={{ fontSize: "12px", color: "#595959", marginTop: "4px", marginLeft: "24px" }}>
                Recomendado para Activos Fijos. Permite asignar números de placa únicos, registrar historial de eventos, mantenimientos y custodios en el módulo de Trazabilidad.
              </div>
            </Col>
            <Col xs={24} sm={6} style={{ textAlign: "right" }}>
              <Tag color="blue">Control Individual</Tag>
            </Col>
          </Row>
        </div>

        {/* ── Sección 2: Categorización ── */}
        <h6 style={{ fontWeight: 600, color: "#595959", marginBottom: "0.75rem" }}>
          Clasificación y Unidad
        </h6>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="Categoría" name="category">
              <Select placeholder="Selecciona categoría">
                {categories.map((v) => (
                  <Option key={v.key} value={v.key}>
                    {v.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="Unidad de medida" name="measure_unit">
              <Select placeholder="Selecciona unidad">
                {ums.map((v) => (
                  <Option key={v.key} value={v.key}>
                    {v.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item label="Presentación" name="display">
              <Select placeholder="Selecciona presentación">
                {presentations.map((v) => (
                  <Option key={v.key} value={v.key}>
                    {v.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "14px 0" }} />

        {/* ── Sección 3: Adquisición y Costos ── */}
        <h5 style={{ fontWeight: 600, fontSize: "1rem", color: "#1f1f1f", marginBottom: "1rem" }}>
          Costos de Adquisición e Impuestos
        </h5>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item
              label="Costo / Precio de Adquisición"
              name="purchase_price"
              rules={[{ required: true, message: "Por favor ingresa el valor de adquisición" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                prefix="$"
                min={0}
                placeholder="0"
                formatter={formatInputNumber}
                parser={parseInputNumber}
                onChange={(val) => handlePriceChange({ purchase_price: val })}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item
              label="Valor Neto de Compra"
              name="purchase_price_net"
              tooltip="Valor base después de aplicar el descuento de compra"
            >
              <InputNumber
                style={{ width: "100%" }}
                prefix="$"
                disabled
                formatter={formatInputNumber}
                parser={parseInputNumber}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={{ span: 8 }}>
            <Form.Item
              label="Costo Total Adquisición"
              name="purchase_price_full"
              tooltip="Costo final considerando impuestos y descuentos aplicados"
            >
              <InputNumber
                style={{ width: "100%" }}
                prefix="$"
                disabled
                formatter={formatInputNumber}
                parser={parseInputNumber}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={{ span: 12 }}>
            <Form.Item
              label="Descuento Compra (%)"
              name="purchase_discount"
              tooltip="Porcentaje de descuento en adquisición (0% - 100%)"
              rules={[{ required: true, message: "Por favor ingrese un valor" }]}
            >
              <InputNumber
                placeholder="0% (Ej. 10)"
                onChange={(val) => handlePriceChange({ purchase_discount: val })}
                suffix="%"
                min={0}
                max={100}
                formatter={formatInputNumber}
                parser={parseInputNumber}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={{ span: 12 }}>
            {purchaseTaxes.map((impuesto, index) => (
              <Row key={impuesto.id} gutter={[10, 10]} align="middle">
                <Col span={16}>
                  <Form.Item
                    label={index === 0 ? "Impuesto de Adquisición" : ""}
                    name={`purchase_taxes_${impuesto.id}`}
                  >
                    <Select
                      allowClear
                      placeholder="Selecciona impuesto"
                      disabled={isFieldDisabled(impuesto.id, "purchase_taxes")}
                      onChange={(val) =>
                        handlePriceChange({ [`purchase_taxes_${impuesto.id}`]: val })
                      }
                    >
                      {purchaseTaxesOptions.map((element) => (
                        <Option
                          key={element.key}
                          value={element.key}
                          disabled={disabledOption(element.key, "purchase_taxes")}
                        >
                          {element.string_value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col
                  span={8}
                  style={{
                    marginTop: index === 0 ? "16px" : "-12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {impuesto.id > 0 && (
                    <Form.Item
                      name={`purchase_taxes_active_${impuesto.id}`}
                      valuePropName="checked"
                      style={{ margin: 0, paddingTop: "8px" }}
                    >
                      <Checkbox
                        onChange={(e) =>
                          handlePriceChange({
                            [`purchase_taxes_active_${impuesto.id}`]: e.target.checked,
                          })
                        }
                      />
                    </Form.Item>
                  )}
                  {purchaseTaxes.length > 0 ? (
                    <MinusCircleOutlined onClick={() => removePurchaseTaxes(impuesto.id)} />
                  ) : null}
                </Col>
              </Row>
            ))}
            {purchaseTaxesWarning && (
              <Alert
                message="Seleccione un valor antes de añadir otro campo"
                banner
                closable
                onClose={() => setPurchaseTaxesWarning(false)}
              />
            )}
            <Button
              type="dashed"
              size="small"
              style={{ marginTop: "5px" }}
              onClick={() =>
                scanForEmptyFields(purchaseTaxes, setPurchaseTaxes, setPurchaseTaxesWarning, "purchase_taxes")
              }
            >
              Agregar Impuesto
            </Button>
          </Col>
        </Row>

        <Divider style={{ margin: "14px 0" }} />

        {/* ── Sección 4: Ficha Técnica y Atributos Adicionales ── */}
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <Collapse
              bordered={false}
              items={[
                {
                  key: "additional-info",
                  label: (
                    <span style={{ fontWeight: 600, color: "#1890ff" }}>
                      <SettingOutlined style={{ marginRight: 6 }} />
                      Ficha Técnica y Datos Personalizados (Marca, Fabricante, Serial de Fábrica, Custodio...)
                    </span>
                  ),
                  children: (
                    <div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c", marginBottom: "10px" }}>
                        Agrega campos personalizados para documentar especificaciones técnicas adicionales como Marca, Potencia, Año de Fabricación, o Responsable.
                      </div>
                      {otherValues.map((valor, index) => (
                        <Row key={valor.id} gutter={[10, 10]} align="middle">
                          <Col span={10}>
                            <Form.Item
                              label={index === 0 ? "Nombre del Campo" : ""}
                              name={`otherValues_type_${valor.id}`}
                            >
                              <Input
                                placeholder="Ej. Marca, Voltaje, Año..."
                                style={{ width: "100%" }}
                                disabled={isFieldDisabled(valor.id, "otherValues_visible")}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              label={index === 0 ? "Valor" : ""}
                              name={`otherValues_value_${valor.id}`}
                            >
                              <Input
                                placeholder="Ej. Caterpillar, 220V, 2024..."
                                style={{ width: "100%" }}
                                disabled={isFieldDisabled(valor.id, "otherValues_visible")}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            span={4}
                            style={{
                              marginTop: index === 0 ? "20px" : "-8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            {valor.id > 0 && (
                              <Form.Item
                                name={`otherValues_visible_${valor.id}`}
                                valuePropName="checked"
                                style={{ margin: 0, paddingTop: "6px" }}
                              >
                                <Checkbox />
                              </Form.Item>
                            )}
                            {otherValues.length > 0 ? (
                              <MinusCircleOutlined onClick={() => removeOtherValues(valor.id)} />
                            ) : null}
                          </Col>
                        </Row>
                      ))}
                      {otherValuesWarning && (
                        <Alert
                          message="Seleccione un valor antes de añadir otro campo"
                          banner
                          closable
                          onClose={() => setOtherValuesWarning(false)}
                        />
                      )}
                      <Button
                        type="dashed"
                        size="small"
                        style={{ marginTop: "5px" }}
                        onClick={() =>
                          scanForEmptyFields(otherValues, setOtherValues, setOtherValuesWarning, "otherValues")
                        }
                      >
                        Agregar Atributo Técnico
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          </Col>
        </Row>
      </div>

      <BarcodeScannerModal
        visible={isScannerOpen}
        onCancel={() => setIsScannerOpen(false)}
        onScan={(result) => {
          form.setFieldsValue({ barcode: result })
        }}
      />
    </>
  )
}

export default RegisterFixedAssetForm
